/**
 * ChatPanel — Kimi answer-first body + always-on Sim right rail.
 *
 * No toggle: the right rail is always present so the user can see what
 * agents are working / what tools ran / current status without having
 * to click. Read-only banner only appears when viewing a past run.
 */
import { memo, useEffect, useMemo, useRef, useState, type ReactNode} from "react";
import { Eye, ChevronDown } from "lucide-react";
import { useMultiAgentStore, type MultiAgentState } from "@/stores/multiAgentStore";
import { useUserIntent, useUserAttachments } from "@/hooks/multi-agent";
import { sanitizeArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";
import { SentAttachments } from "@/components/molecules/multi-agent/SentAttachments";
import { ReportExportBar } from "@/components/molecules/multi-agent/ReportExportBar";
import { fixMermaidUnquotedLabels } from "@/lib/studio/mermaidScrub";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { ResultColumn, buildArtifactInterceptor } from "@/components/organisms/multi-agent/ResultColumn";
import { ArtifactCard } from "@/components/molecules/multi-agent/ArtifactCard";
import { MessageFeedback } from "@/components/molecules/multi-agent/MessageFeedback";
import { RightRail } from "@/components/organisms/multi-agent/RightRail";
import { LIVE_STATUSES } from "@/constants/multi-agent/status.constants";

type ThreadTurn = MultiAgentState["thread"][number];
type ArtifactRow = MultiAgentState["artifacts"][number];

/**
 * PastTurn — one finished Q→A turn rendered above the active run.
 *
 * WHY memo'd: ChatPanel re-renders frequently while a NEW run streams (status,
 * thread identity, etc.). Previously the past-turn body lived inline in
 * `thread.map(...)`, so every one of those re-renders rebuilt each turn's
 * markdown `content` + artifact `interceptor`/`components` as brand-new values
 * and handed them to <SkillResultMarkdown>. That defeated SkillResultMarkdown's
 * `memo` — Streamdown re-parsed on each pass and DROPPED already-rendered
 * mermaid diagrams. Extracting into a memo'd component keyed on stable inputs
 * (runId + finalMarkdown + artifacts identity) means a status re-render of the
 * parent no longer cascades into every past turn.
 *
 * It also restores the export bar on past turns (via <ReportExportBar>), which
 * the old inline renderer never had.
 */
const PastTurn = memo(function PastTurn({
  turn,
  artifacts,
}: {
  turn: ThreadTurn;
  artifacts: ArtifactRow[];
}) {
  // This turn's OWN artifacts: prefer the runId tag (so each Q→A shows its own
  // results/diff), fall back to the snapshot's artifactIds for older/untagged rows.
  const turnArtifacts = useMemo(
    () =>
      artifacts.filter((a) =>
        a.runId ? a.runId === turn.runId : turn.artifactIds.includes(a.id),
      ),
    [artifacts, turn.runId, turn.artifactIds],
  );

  // Re-render this PAST turn with the SAME artifact interceptor the live column
  // uses, fed from THIS turn's artifacts — so its citation chips resolve and its
  // diff/code cards render (instead of inert text). Keyed on stable inputs so
  // the content string + components identity only change when the turn's data
  // actually changes — NOT on every ChatPanel status re-render.
  const content = useMemo(
    () => fixMermaidUnquotedLabels(sanitizeArtifactMarkdown(turn.finalMarkdown)),
    [turn.finalMarkdown],
  );
  const interceptor = useMemo(
    () => buildArtifactInterceptor(turnArtifacts).interceptor,
    [turnArtifacts],
  );

  return (
    <div className="ma-prose" style={{ maxWidth: "100%", padding: 0 }} data-ma-report-root>
      <SkillResultMarkdown
        content={content}
        components={interceptor as unknown as Record<string, unknown>}
      />
      {/* Working client-side export for this past turn (md / PDF / Word) — the
          strip that used to vanish once the answer left ResultColumn. */}
      {turn.finalMarkdown.trim() && (
        <ReportExportBar markdown={turn.finalMarkdown} title={turn.intent} />
      )}
      {turnArtifacts.length > 0 && (
        <div className="mt-3 space-y-2">
          {turnArtifacts.map((a) => (
            <LazyMount key={a.id} minHeight={44}>
              <ArtifactCard artifact={a} />
            </LazyMount>
          ))}
        </div>
      )}
      {/* Per-turn feedback — same component as the live ResultColumn uses.
          State is local to this component instance so each turn has its own
          independent submitted/open state across the thread. */}
      {turn.finalMarkdown.trim() && <MessageFeedback runId={turn.runId} />}
    </div>
  );
});

/**
 * LazyMount — defers rendering its children until the placeholder scrolls near
 * the viewport (400px margin). Past-turn artifact cards open by default, which
 * would otherwise mount a Monaco editor per card across every turn (100+ at
 * once). This keeps "open" semantics but only mounts editors that are actually
 * near the screen — scrolling mounts more on demand.
 */
function LazyMount({ children, minHeight = 44 }: { children: ReactNode; minHeight?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || show) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  return (
    <div ref={ref} style={{ minHeight: show ? undefined : minHeight }}>
      {show ? children : null}
    </div>
  );
}


export function ChatPanel({ hideRail = false }: { hideRail?: boolean } = {}) {
  const intent = useUserIntent();
  const intentAttachments = useUserAttachments();
  const readOnly = useMultiAgentStore((s) => s.readOnly);
  const thread = useMultiAgentStore((s) => s.thread);
  const status = useMultiAgentStore((s) => s.status);
  // Persisted artifacts (union across the session) — used to re-render each PAST
  // turn's code/diff/citations from its own artifactIds, so previous runs keep
  // their diff cards + artifact chips instead of collapsing to plain text.
  const allArtifacts = useMultiAgentStore((s) => s.artifacts);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Fresh/empty chat (no prior turns, no active question, idle) — vertically
  // centre the idle hero instead of pinning it to the top, which otherwise
  // leaves a large empty gap above the input. Safe: empty state never overflows.
  const isFresh = thread.length === 0 && !intent.trim() && status === "idle";
  const [showJumpButton, setShowJumpButton] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  // Tracks explicit user scroll-up intent. Follow mode is disabled while true,
  // preventing the viewport from yanking back during streaming. Cleared when
  // the user scrolls back near the bottom or clicks the jump button.
  const userScrolledUpRef = useRef(false);

  // Show/hide the jump button and detect when the user returns to the bottom
  // (which re-enables follow mode).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // Resume follow mode when user scrolls back near the bottom.
      if (distanceFromBottom <= 80) userScrolledUpRef.current = false;
      setShowJumpButton(distanceFromBottom > 120);
    };
    // Detect intentional upward scroll — suspends follow mode immediately.
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) userScrolledUpRef.current = true;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
    };
  }, []);

  // Autoscroll as the answer streams. Follow mode uses an explicit intent ref
  // (not a distance threshold) so it can't be accidentally disabled by content
  // pushing the viewport slightly away from the bottom before the first scroll.
  // Driven by a transient store subscription so streaming tokens don't re-render
  // this panel (and the thread list + right rail) on every token.
  useEffect(() => {
    if (readOnly) return;
    let frame = 0;
    const scrollToBottom = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    };
    const scrollIfFollowing = () => {
      if (userScrolledUpRef.current) return;
      scrollToBottom();
    };
    scrollIfFollowing();
    const unsubscribe = useMultiAgentStore.subscribe((s, prev) => {
      const nowLive = LIVE_STATUSES.has(s.status);
      const wasLive = LIVE_STATUSES.has(prev.status);
      // Sync streaming flag for the jump button label.
      if (nowLive !== wasLive) setIsStreaming(nowLive);
      // New run started — re-enable follow mode and jump to bottom regardless
      // of where the user was scrolled (they just clicked Send).
      if (!wasLive && nowLive) {
        userScrolledUpRef.current = false;
        scrollToBottom();
        return;
      }
      if (
        s.finalMarkdown !== prev.finalMarkdown ||
        s.textBuffer !== prev.textBuffer ||
        s.thread !== prev.thread ||
        s.status !== prev.status
      ) {
        scrollIfFollowing();
      }
    });
    return () => {
      cancelAnimationFrame(frame);
      unsubscribe();
    };
  }, [readOnly]);


  return (
    <div className="flex h-full w-full min-w-0 flex-col overflow-hidden">
      {readOnly && (
        <div
          className="flex items-center gap-2 px-5 py-1.5"
          style={{
            background: "color-mix(in oklab, var(--ma-state-warn) 10%, transparent)",
            fontSize: "var(--ma-fs-micro)",
            color: "var(--ma-text)",
          }}
        >
          <Eye className="h-3.5 w-3.5" />
          <span>Viewing a past run — read only.</span>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-row overflow-hidden">
        {/* Main answer column — scrollable so prior turns stack above
            the active run and the user scrolls through the conversation. */}
        <div
          ref={scrollRef}
          className={`flex min-w-0 flex-1 flex-col ${isFresh ? "overflow-hidden" : "overflow-auto"}`}
        >
          {/* Prior turns — earlier Q&A in this chat session, rendered
              compactly above the active run. Each is a quiet block:
              intent + final answer + a faint divider. */}
          {thread.map((t) => (
            <div
              key={t.runId}
              className="mx-auto w-full ma-fade-up"
              style={{
                maxWidth: "var(--ma-prose-max)",
                padding: "24px 24px 20px",
                borderBottom: "1px solid color-mix(in oklab, var(--ma-border) 72%, transparent)",
              }}
            >
              {/* User message bubble */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    maxWidth: "80%",
                    background: "color-mix(in oklab, hsl(var(--primary)) 10%, hsl(var(--card)))",
                    border: "1px solid color-mix(in oklab, hsl(var(--primary)) 22%, var(--ma-border))",
                    borderRadius: "var(--ma-radius-md) var(--ma-radius-md) 4px var(--ma-radius-md)",
                    padding: "10px 14px",
                    fontSize: "var(--ma-fs-body)",
                    fontWeight: 450,
                    lineHeight: 1.55,
                    color: "var(--ma-text)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  <SentAttachments attachments={t.attachments} />
                  {t.intent}
                </div>
              </div>
              {/* Memo'd past-turn body — see PastTurn above. Rendering it as a
                  memo'd child (instead of an inline IIFE) stops ChatPanel's
                  frequent status re-renders from re-parsing this turn's markdown
                  and dropping its mermaid, and restores the export bar. */}
              <PastTurn turn={t} artifacts={allArtifacts} />

              {/* What the agent did this turn — collapsible. Empty turns
                  (where we didn't capture activity) skip it. */}
              {(t.activity.length > 0 || t.reasoning) && (
                <details
                  className="mt-4"
                  style={{
                    background: "var(--ma-surface-soft)",
                    borderRadius: "var(--ma-radius-sm)",
                    padding: "6px 10px",
                  }}
                >
                  <summary
                    className="ma-micro cursor-pointer"
                    style={{ color: "var(--ma-text-muted)", listStyle: "none" }}
                  >
                    What the agent did
                    {t.activity.length > 0 && ` · ${t.activity.length} events`}
                    {t.artifactIds.length > 0 && ` · ${t.artifactIds.length} artifacts`}
                  </summary>
                  {t.activity.length > 0 && (
                    <ul
                      className="m-0 mt-2 flex flex-col gap-1 p-0"
                      style={{ listStyle: "none", fontSize: 11, color: "var(--ma-text-muted)" }}
                    >
                      {t.activity.map((e, index) => (
                        <li key={`${t.runId}-${e.id}-${index}`} className="truncate" title={e.text}>
                          {e.tool ? (
                            <>
                              <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--ma-text)" }}>
                                {e.tool}
                              </span>
                              {e.argsSummary && <span> ({e.argsSummary})</span>}
                              {typeof e.durationMs === "number" && (
                                <span style={{ marginLeft: 6, fontVariantNumeric: "tabular-nums" }}>
                                  {e.durationMs}ms
                                </span>
                              )}
                            </>
                          ) : e.text}
                        </li>
                      ))}
                    </ul>
                  )}
                  {t.reasoning && (
                    <div
                      className="mt-2"
                      style={{
                        fontSize: 11,
                        fontStyle: "italic",
                        color: "var(--ma-text-muted)",
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        maxHeight: 140,
                        overflowY: "auto",
                      }}
                    >
                      {t.reasoning}
                    </div>
                  )}
                </details>
              )}
            </div>
          ))}

          {/* Active run user message — right-aligned bubble matching thread turns. */}
          {intent && (
            <div
              className="mx-auto w-full"
              style={{ maxWidth: "var(--ma-prose-max)", padding: "24px 24px 8px" }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div
                  style={{
                    maxWidth: "80%",
                    background: "color-mix(in oklab, hsl(var(--primary)) 10%, hsl(var(--card)))",
                    border: "1px solid color-mix(in oklab, hsl(var(--primary)) 22%, var(--ma-border))",
                    borderRadius: "var(--ma-radius-md) var(--ma-radius-md) 4px var(--ma-radius-md)",
                    padding: "10px 14px",
                    fontSize: "var(--ma-fs-body)",
                    fontWeight: 450,
                    lineHeight: 1.55,
                    color: "var(--ma-text)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  <SentAttachments attachments={intentAttachments} />
                  {intent}
                </div>
              </div>
            </div>
          )}

          {/* The answer for the active run. When the chat is fresh/empty the
              wrapper grows (flex-1) so the idle hero can centre vertically;
              otherwise it stays content-sized so answers stack from the top. */}
          <div className={isFresh ? "flex h-full min-h-0 flex-1 flex-col" : "min-h-0 flex-shrink-0"}>
            <ResultColumn embedded />
          </div>

          {/* Jump-to-latest — sticky row spans full column width (required
              for sticky), pointer-events-none so prose below stays clickable.
              float:right is ignored inside flex, so justify-content:flex-end
              pushes the pill to the right edge of the chat column. */}
          {showJumpButton && (
            <div
              style={{
                position: "sticky",
                bottom: 16,
                zIndex: 10,
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: 16,
                pointerEvents: "none",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  userScrolledUpRef.current = false;
                  scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
                }}
                aria-label="Jump to latest"
                style={{
                  pointerEvents: "auto",
                  height: 32,
                  minWidth: 32,
                  borderRadius: 999,
                  background: "var(--ma-surface)",
                  border: "1px solid var(--ma-border)",
                  boxShadow: "var(--ma-shadow-md)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  padding: isStreaming ? "0 12px" : "0",
                  color: "var(--ma-text-muted)",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                <ChevronDown className="h-4 w-4" style={{ flexShrink: 0 }} />
                {isStreaming && <span>New messages</span>}
              </button>
            </div>
          )}
        </div>

        {/* Right rail — hidden when the host renders Run details elsewhere
            (the multi-agent IDE moves it into the rightmost panel column). */}
        {!hideRail && (
          <div
            className="w-[340px] max-w-[40vw]"
            style={{
              borderLeft: "1px solid var(--ma-border)",
              background: "var(--ma-bg)",
            }}
          >
            <RightRail />
          </div>
        )}
      </div>
    </div>
  );
}
